var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import globToRegExp from 'glob-to-regexp';
import { ExternalResolvePromise } from '../models/external-resolve-promise';
/**
 * NOTE: There are some debug functions you can call from the console
 * to print out information from the service worker.
 * navigator.serviceWorker.controller.postMessage({ type: 'DEBUG_LOG_TIMEOUT_REGISTRY' });
 * navigator.serviceWorker.controller.postMessage({ type: 'DEBUG_LOG_L1_CACHE' });
 */
// the root name for our cache
const CACHE_ROOT = 'ps-mfe';
const STATIC_CACHE_ROOT = 'static-ps-mfe';
const importKeyAlgorithm = {
    name: 'AES-GCM',
    length: 256
};
const keyUsages = [
    'encrypt',
    'decrypt',
];
let serviceWorkerEncryptionKeys;
let dynamicCacheName = '';
const callsInProgress = {};
//We will store Response.json() on it.
const l1Cache = {};
// Although this function is only one line for now it will allow us to add more code to the
// Install event as needed later on
export function setFullCacheName(cacheNameSuffix) {
    dynamicCacheName = `${CACHE_ROOT}-${cacheNameSuffix}`;
}
export function createStaticCache(staticCacheName, fullCacheFilesList) {
    const STATIC_CACHE_NAME = `${STATIC_CACHE_ROOT}-${staticCacheName}`;
    caches.open(STATIC_CACHE_NAME)
        .then((cache) => {
        return cache.addAll(fullCacheFilesList);
    })
        .catch((error) => {
        console.error('There was a problem setting up the Static Service Worker Cache');
    });
}
/**
 *
 * @param encryptionKeys This is the object that we want to validate as a properly formatted EncryptionKeys object
 * @returns true if the keys are valid, and false if they are not
 * NOTE: This is not 100% validation. We are simply verifying the types and that strings are not empty. However, a given key could be the incorrect format / length still
 * TODO: update more specifically to validate exact encrpytion key length and type (there is somewhere you can find the correct format for these types of keys)
 */
export function isEncryptionKeysValid(encryptionKeys) {
    let isValid = true;
    if (encryptionKeys != null) {
        if (typeof encryptionKeys.activeKeyId !== 'string' || encryptionKeys.activeKeyId.trim().length === 0) {
            console.error('The encryption key active id is not a valid string: ', encryptionKeys.activeKeyId);
            isValid = false;
        }
        if (!(encryptionKeys.keys instanceof Array) || encryptionKeys.keys.length === 0) {
            console.error('The encryption keys are not valid: ', encryptionKeys.keys);
            isValid = false;
        }
        else {
            for (const keyObj of encryptionKeys.keys) {
                if (typeof keyObj.keyId !== 'string' || keyObj.keyId.trim().length === 0) {
                    console.error('An Encryption key id is not valid: ', keyObj.keyId);
                    isValid = false;
                }
                if (typeof keyObj.value !== 'string' || keyObj.value.trim().length === 0) {
                    console.error('An Encryption key value is not valid: ', keyObj.value);
                    isValid = false;
                }
            }
        }
    }
    else {
        console.error('The Encrpytion Keys being validated are undefined');
        isValid = false;
    }
    return isValid;
}
/**
 * This function creates the function to pass into self.addEventListener('fetch', createFetchInterceptor(...)).  It handles intercepting API calls and returns cached data if it's
 * in the list of APIs that are supposed to be cached.
 * @param cacheUrlTimeouts - Pass in an empty object here and keep a reference to it. You will need to pass that same object into the updateCacheTimeoutsMessageListener() function.
 * @param cacheNameSuffix - The name of the cache to store results into.
 * @param staticCacheName - This is no longer used, an empty string may be entered
 * @param fullCacheFilesListMap - A map of files that the application caches on install.
 * @param encryptionKeys - Object containing keys to be used for Encryption, this also can be set with the function setEncryptionKeys in http.ts
 * @returns A callback function to pass into a service worker fetch event listener.
 */
export function createFetchInterceptor(cacheUrlTimeouts, cacheNameSuffix, staticCacheName, fullCacheFilesListMap, encryptionKeys) {
    if (encryptionKeys != null && isEncryptionKeysValid(encryptionKeys)) {
        serviceWorkerEncryptionKeys = encryptionKeys;
    }
    //NOTE: This function is not called if prefetch function found the data in the L1 cache.
    return function fetchInterceptor(fetchEvent) {
        //It returns 'true' or it's not.
        const superFetchHeader = fetchEvent.request.headers.get('superFetch');
        if (superFetchHeader === 'true' && serviceWorkerEncryptionKeys != null) {
            let superFetchTagList = [];
            try {
                const parsedTagList = JSON.parse(fetchEvent.request.headers.get('superFetchTagList') || '[]');
                if (parsedTagList instanceof Array) {
                    superFetchTagList = parsedTagList;
                }
            }
            catch (e) {
                console.error('The following error simply means that no tagList was transferred to the service worker in superFetch');
                console.error(e);
            }
            fetchEvent.respondWith(isInCacheUrlTimeoutsList(fetchEvent.request.url, cacheUrlTimeouts).then((isFound) => {
                if (isFound) {
                    //This is what we want to do:
                    // Fetch data
                    // Store the data in the L1 cache
                    // Encrypt data and put it in the L2 cache
                    // return the response
                    return getFreshTimeStampedResponseAndCache(fetchEvent.request, cacheNameSuffix, cacheUrlTimeouts, superFetchTagList);
                }
                else {
                    console.warn('The url was not registered using registerTimeouts().  superFetch will not cache anything without registration. Please use registerTimeouts() for the url so superFetch can cache it.', fetchEvent.request.url);
                    // URL was not registered, so we fetch normally
                    return fetchAndRetry(fetchEvent.request).then((freshResponse) => {
                        return addNetworkcallTimestampToFreshResponse(freshResponse);
                    });
                }
            }).then((response) => {
                if (callsInProgress[fetchEvent.request.url] != null) {
                    (callsInProgress[fetchEvent.request.url]).externalResolve(response.clone());
                    //Now that this is over, we delete the call in progress.
                    delete callsInProgress[fetchEvent.request.url];
                }
                return response.clone();
            }).catch((error) => {
                if (callsInProgress[fetchEvent.request.url] != null) {
                    (callsInProgress[fetchEvent.request.url]).externalReject(error);
                    //Now that this is over, we delete the call in progress.
                    delete callsInProgress[fetchEvent.request.url];
                }
            }));
        }
        else if (fullCacheFilesListMap[fetchEvent.request.url]) {
            //Checking files cache for url: fetchEvent.request.url
            fetchEvent.respondWith(caches.match(fetchEvent.request).then((response) => {
                if (response) {
                    // Return from cache
                    return response;
                }
                else {
                    //Otherwise tell the browser to go get the resource from the network
                    return fetchAndRetry(fetchEvent.request);
                }
            }));
        }
        else {
            if (superFetchHeader === 'true' && serviceWorkerEncryptionKeys == null) {
                console.warn('Encryption keys are required in order to cache superFetch data. See mfe-utils README.md for instructions on setting up encryption keys. Url called: ', fetchEvent.request.url);
            }
            // Url was not on the interceptor list, on the cached file list or was missing required parameters (encrpytionKeys and using superfetch)
            // So, we fetch normally with no modifications to the Response or Response Payload
            fetchEvent.respondWith(fetchAndRetry(fetchEvent.request).then((normalFetchResponse) => {
                if (superFetchHeader === 'true') {
                    // TODO: Should we put the response in the L1 cache here?
                    // When using superFetch the networkCallTimeStamp is expected, so we send it back with the expected object type
                    return addNetworkcallTimestampToFreshResponse(normalFetchResponse).then((freshResponseWithTimeStamp) => {
                        if (callsInProgress[fetchEvent.request.url] != null) {
                            callsInProgress[fetchEvent.request.url].externalResolve(freshResponseWithTimeStamp.clone());
                            delete callsInProgress[fetchEvent.request.url];
                        }
                        return freshResponseWithTimeStamp.clone();
                    });
                }
                else {
                    return normalFetchResponse;
                }
            }).catch((error) => {
                if (callsInProgress[fetchEvent.request.url] != null) {
                    (callsInProgress[fetchEvent.request.url]).externalReject(error);
                    //Now that this is over, we delete the call in progress.
                    delete callsInProgress[fetchEvent.request.url];
                }
            }));
        }
    };
}
/**
 * @deprecated Use handleMfeUtilsServiceWorkerMessages instead
 */
export function updateCacheTimeoutsMessageListener(cacheUrlTimeouts) {
    return handleMfeUtilsServiceWorkerMessages(cacheUrlTimeouts);
}
/**
 * Call this function to setup the message listener on your service worker. It allows the application to set timeouts on APIs that get intercepted in the service worker.
 * It also allows us to set the encryption keys from anywhere in our application
 * @param cacheUrlTimeouts - The same object that was passed into createFetchInterceptor()
 * @returns A callback function to be passed into the message listener.  self.addEvent('message', updateCacheTimeoutsMessageListener(...))
 *
 * NOTE: There are some debug functions you can call from the console to print out information from the srvice worker.
 * navigator.serviceWorker.controller.postMessage({ type: 'DEBUG_LOG_TIMEOUT_REGISTRY' });
 * navigator.serviceWorker.controller.postMessage({ type: 'DEBUG_LOG_L1_CACHE' });
 */
export function handleMfeUtilsServiceWorkerMessages(cacheUrlTimeouts) {
    return (event) => {
        if (event == null) {
            console.error('There was no event passed into the handleMfeUtilsServiceWorkerMessages in mfe-utils.  It cannot function without it. Exiting the function');
            return;
        }
        //We don't have the typings for service workers.
        //The event type REALLY is a ExtendableMessageEvent
        //The source property on an ExtendableMessageEvent is WindowClient object which is a Window.
        const currentClientWindow = event.source;
        if (currentClientWindow == null) {
            console.error('The event did not have a valid source object to send the message back to.', event);
            return;
        }
        if (currentClientWindow.type !== 'window') {
            console.error('We got a message from something other than a window so we are not going to deal with it', currentClientWindow.type, event);
            return;
        }
        if (event.data.type === 'UPDATE_ENCRYPTION_KEYS') {
            // Here we can access the cache to check for missing keys and then remove the data from them as needed
            // We will use a timeout before starting this operation, let's try 5 seconds.
            // Add a comment to this updated code that talks about how this was an intial plan
            // and we can modify it if any performance issues come up from this code
            // Specifically we can walk through in chunks. Comment Date: 6/30/2023
            if (isEncryptionKeysValid(event.data.encryptionKeys)) {
                // We need to check if we already have encryption keys. If we sign out and sign in as another user in the same tab, the service worker retains this value.
                // Even upon user change
                if (serviceWorkerEncryptionKeys != null) {
                    for (const incomingKey of event.data.encryptionKeys.keys) {
                        const encryptionKey = serviceWorkerEncryptionKeys.keys.find((key) => {
                            return incomingKey.keyId === key.keyId;
                        });
                        if (encryptionKey == null) {
                            // There is a new key. This means we either have a new user, keys are compromised, or we haven't logged in for 90 days.
                            // In the first case it's essential we clear the L1 cache immediately since it isn't encrypted.
                            // This prevents the case where users see other users data
                            for (const key of Object.keys(l1Cache)) {
                                delete l1Cache[key];
                            }
                            break;
                        }
                    }
                }
                serviceWorkerEncryptionKeys = event.data.encryptionKeys;
            }
            getDynamicCacheName().then((dynamicCacheName) => {
                caches.open(dynamicCacheName).then((cache) => {
                    // Cache keys are the intercepted requests, they contain the URL of the request
                    cache.keys().then((keys) => {
                        keys.map((request) => {
                            cache.match(request).then((cacheResponse) => {
                                if (cacheResponse == null) {
                                    return;
                                }
                                else {
                                    cacheResponse.clone().json().then((encryptedPayload) => {
                                        // Everything below here needs to go in the then with the encryptedPayload
                                        const encryptionKeyValueAsArrayBuffer = getRawFormatKey(encryptedPayload.encryptionKeyId);
                                        if (encryptionKeyValueAsArrayBuffer == null) {
                                            // SECURITY RISK MITIGATION
                                            // This could mean someone new logged in or a key is compromised, delete the L1 cache
                                            // Keys could become compromised, and if that happens we want
                                            // to remove the data using compromised or inactive keys as soon as possible
                                            cache.delete(request);
                                        }
                                    });
                                }
                            });
                        });
                    });
                });
            });
            currentClientWindow.postMessage({
                type: 'UPDATED_ENCRYPTION_KEYS'
            });
        }
        else if (event.data.type === 'UPDATE_CACHE_TIMEOUTS') {
            const urlTimeoutData = event.data.timeouts[0]; //event.data.timeouts is only an array at this time to prevent regression. There should always be only one element in the array.
            cacheUrlTimeouts[urlTimeoutData.url] = Object.assign(Object.assign({}, urlTimeoutData.timeouts), { urlRegex: urlTimeoutData.urlRegex });
            currentClientWindow.postMessage({
                type: 'UPDATED_CACHE_TIMEOUTS',
                urlGlob: urlTimeoutData.url,
                stale: urlTimeoutData.timeouts.stale,
                expires: urlTimeoutData.timeouts.expires
            });
        }
        else if (event.data.type === 'DELETE_FROM_DYNAMIC_CACHE') {
            getDynamicCacheName().then((dynamicCacheName) => {
                deleteFromCacheByUrl(dynamicCacheName, event.data.urlGlob).then((isDeleted) => {
                    // Send a response - the clients
                    // array is ordered by last focused
                    currentClientWindow.postMessage({
                        type: 'DELETED_FROM_DYNAMIC_CACHE',
                        isDeleted: isDeleted
                    });
                });
            });
        }
        else if (event.data.type === 'PRE_FETCH') {
            //   CASE: No Data
            //   return undefined;
            //   CASE: Up to date Data
            //   return preFetchResult;
            //   CASE: Stale Data
            //   return preFetchResult;
            //   CASE: Expired Data
            //   return undefined;
            getFromCache(event.data.url).then((prefetchPayload) => {
                let isRegistered = false;
                let expiredTimeout = 0;
                let staleTimeout = 0;
                const registryEntry = searchCacheUrlTimeouts(event.data.url, cacheUrlTimeouts);
                if (registryEntry != null) {
                    isRegistered = true;
                    expiredTimeout = registryEntry.expires;
                    staleTimeout = registryEntry.stale;
                }
                else {
                    console.error(`superfetch was called on the url "${event.data.url}" which did not match any patterns previously added by registerTimeouts(). Please ensure that you called registerTimeouts() for the url and that you wait for the promise to complete before making the call to superfetch.`);
                }
                if (!isRegistered || prefetchPayload == null || prefetchPayload.cacheTimeStamp == null || isExpired(prefetchPayload.cacheTimeStamp, expiredTimeout)) {
                    //Case: The data is not in the cache or in transit or it is expired
                    if (callsInProgress[event.data.url] != null) {
                        // Case: The data is in transit
                        callsInProgress[event.data.url].promise.then((resolvedResponse) => {
                            const contentType = resolvedResponse.headers.get('content-type');
                            if (resolvedResponse.status >= 200 && resolvedResponse.status < 300 && (contentType === null || contentType === void 0 ? void 0 : contentType.indexOf('application/json')) !== -1) {
                                resolvedResponse.clone().json().then((responsePayload) => {
                                    //Variable created here so we have type checking.
                                    const message = {
                                        type: 'PRE_FETCH_RESPONSE',
                                        payload: responsePayload,
                                        case: 'DATA_FROM_IN_FLIGHT_FETCH',
                                        prefetchPid: event.data.prefetchPid,
                                        responseIsStale: false
                                    };
                                    currentClientWindow.postMessage(message);
                                });
                            }
                            else {
                                serializeResponse(resolvedResponse.clone()).then((serializedResponse) => {
                                    // Variable created here so we have type checking.
                                    const message = {
                                        type: 'PRE_FETCH_RESPONSE',
                                        payload: serializedResponse,
                                        case: 'DATA_IN_FLIGHT_WITH_FULL_RESPONSE_OBJECT_RETURNED',
                                        prefetchPid: event.data.prefetchPid,
                                        responseIsStale: false
                                    };
                                    currentClientWindow.postMessage(message);
                                });
                            }
                        });
                    }
                    else {
                        if (isRegistered) {
                            //We know that if it's registered that as soon as this message gets back to the main thread
                            //that the main thread is going to kick off a fetch operation.
                            //Therefore, we need to create a promise here.
                            callsInProgress[event.data.url] = new ExternalResolvePromise();
                        }
                        //Variable created here so we have type checking.
                        const message = {
                            type: 'PRE_FETCH_RESPONSE',
                            case: 'NO_DATA_FROM_PRE_FETCH',
                            prefetchPid: event.data.prefetchPid,
                            responseIsStale: false
                        };
                        currentClientWindow.postMessage(message);
                    }
                }
                else {
                    //prefetchPayload != null will be true
                    //Variable created here so we have type checking.
                    if (isStale(prefetchPayload.cacheTimeStamp, staleTimeout)) {
                        if (callsInProgress[event.data.url] == null) {
                            callsInProgress[event.data.url] = new ExternalResolvePromise();
                            const message = {
                                type: 'PRE_FETCH_RESPONSE',
                                case: 'DATA_FROM_CACHE',
                                payload: prefetchPayload,
                                prefetchPid: event.data.prefetchPid,
                                responseIsStale: isStale(prefetchPayload.cacheTimeStamp, staleTimeout)
                            };
                            currentClientWindow.postMessage(message);
                        }
                        else {
                            const message = {
                                type: 'PRE_FETCH_RESPONSE',
                                case: 'DATA_FROM_CACHE',
                                payload: prefetchPayload,
                                prefetchPid: event.data.prefetchPid,
                                responseIsStale: isStale(prefetchPayload.cacheTimeStamp, staleTimeout),
                                isInProgress: true
                            };
                            currentClientWindow.postMessage(message);
                        }
                    }
                    else {
                        //This is the case where we got data from the cache and it isn't stale and it isn't expired
                        //So we are just going to return it.
                        //Before we do so we might need to update the superFetchTagList in the cache
                        //To do so will require us to do the following
                        //1) Check to see if the existing superFetchTagList needs to be modified
                        //  a) Check L1 Cache
                        //  b) Check L2 Cache
                        //2) Any tags from the current call that aren't in the tagList need to be added
                        let awaitCacheUpdate = Promise.resolve();
                        const l1CacheEntry = l1Cache[event.data.url];
                        if (l1CacheEntry != null) {
                            if (arrayNeedsUpdate(l1CacheEntry.superFetchTagList, event.data.superFetchTagList)) {
                                //Updates the L1 Cache superFetchTagList
                                l1CacheEntry.superFetchTagList = combineArrays(l1CacheEntry.superFetchTagList, event.data.superFetchTagList);
                                awaitCacheUpdate = getDynamicCacheName().then((dynamicCacheName) => {
                                    caches.open(dynamicCacheName).then((cache) => {
                                        return cache.match(event.data.url).then((response) => {
                                            if (response == null) {
                                                return;
                                            }
                                            return response.json().then((timeStampedEncryptedPayload) => {
                                                if (timeStampedEncryptedPayload == null) {
                                                    return;
                                                }
                                                //combine the arrays for the superFetchTagList
                                                timeStampedEncryptedPayload.superFetchTagList = combineArrays(timeStampedEncryptedPayload.superFetchTagList, l1CacheEntry.superFetchTagList);
                                                //now that the taglist is updated we need to put it back into the cache
                                                const jsonBlob = new Blob([JSON.stringify(timeStampedEncryptedPayload)], { type: 'application/json' });
                                                const init = { 'status': response.status, 'statusText': response.statusText, 'headers': response.headers };
                                                //Update the L2 cache with the updated superFetchTagList
                                                return cache.put(event.data.url, new Response(jsonBlob, init));
                                            });
                                        });
                                    });
                                });
                            }
                        }
                        awaitCacheUpdate.then(() => {
                            const message = {
                                type: 'PRE_FETCH_RESPONSE',
                                case: 'DATA_FROM_CACHE',
                                payload: prefetchPayload,
                                prefetchPid: event.data.prefetchPid,
                                responseIsStale: isStale(prefetchPayload.cacheTimeStamp, staleTimeout)
                            };
                            currentClientWindow.postMessage(message);
                        });
                    }
                }
            });
        }
        else if (event.data.type === 'DELETE_CACHE_FOR_TAGS') {
            if (typeof event.data.tagMatchRegExpString === 'string') {
                const tagMatchRegex = new RegExp(event.data.tagMatchRegExpString, event.data.tagMatchRegExpFlags || undefined);
                const start = Date.now();
                getDynamicCacheName().then((dynamicCacheName) => {
                    return caches.open(dynamicCacheName).then((cache) => {
                        return Promise.all(Object.keys(l1Cache).map((key) => {
                            //First take care of the L1 Cache
                            const l1CacheEntry = l1Cache[key];
                            if (l1CacheEntry.superFetchTagList == null) {
                                return Promise.resolve(false);
                            }
                            const hasMatchingTag = l1CacheEntry.superFetchTagList.some((superFetchTag) => {
                                return tagMatchRegex.test(superFetchTag);
                            });
                            if (hasMatchingTag) {
                                return cache.delete(key).then(() => {
                                    //Normally we don't delete things from arrays while we are looping through them.
                                    //However, this isn't happening synchronously because it's deleting from the hard disk, which is async,
                                    //so l1Cache won't actually be modified until after the loop has finished.
                                    delete l1Cache[key];
                                });
                            }
                            else {
                                return Promise.resolve(false);
                            }
                        })).then(() => {
                            //Next take care of the L2 Cache
                            return cache.keys().then((requests) => {
                                return Promise.all(requests.map((request) => {
                                    return cache.match(request.url).then((response) => {
                                        return response.json();
                                    }).then((responseObject) => {
                                        //The responseObject might be a TimeStampedEncryptedPayload, but then again, it might not be.
                                        if (responseObject.superFetchTagList instanceof Array) {
                                            const hasMatchingTag = responseObject.superFetchTagList.some((superFetchTag) => {
                                                return tagMatchRegex.test(superFetchTag);
                                            });
                                            if (hasMatchingTag) {
                                                //Deletes the cached entry from the L2 cache
                                                return cache.delete(request);
                                            }
                                            else {
                                                return Promise.resolve();
                                            }
                                        }
                                        else {
                                            return Promise.resolve();
                                        }
                                    });
                                }));
                            });
                        });
                    });
                }).then(() => {
                    console.log('Time to complete delete from cache operation is ' + (Date.now() - start) + ' milliseconds.');
                    const message = {
                        type: 'DELETE_CACHE_FOR_TAGS_RESPONSE',
                        isDeleted: true
                    };
                    currentClientWindow.postMessage(message);
                }).catch(() => {
                    const message = {
                        type: 'DELETE_CACHE_FOR_TAGS_RESPONSE',
                        isDeleted: false
                    };
                    currentClientWindow.postMessage(message);
                });
            }
        }
        else if (event.data.type === 'DEBUG_LOG_TIMEOUT_REGISTRY') {
            // Run this section by calling this in the browser console:
            // navigator.serviceWorker.controller.postMessage({ type: 'DEBUG_LOG_TIMEOUT_REGISTRY' });
            console.log('* * * * *   cacheUrlTimeouts   * * * * *');
            for (const pattern of Object.keys(cacheUrlTimeouts)) {
                console.log(`pattern: ${pattern}`);
                if (cacheUrlTimeouts[pattern] != null) {
                    console.log(`  stale: ${cacheUrlTimeouts[pattern].stale}`);
                    console.log(`  expires: ${cacheUrlTimeouts[pattern].expires}\n\n`);
                }
                else {
                    console.log('  no CacheTimeoutRecord\n\n');
                }
            }
            console.log('* * * * * * * * * * * * * * * * * * * * *');
        }
        else if (event.data.type === 'DEBUG_LOG_L1_CACHE') {
            // Run this section by calling this in the browser console:
            // navigator.serviceWorker.controller.postMessage({ type: 'DEBUG_LOG_L1_CACHE' });
            console.log('* * * * * * *  l1Cache   * * * * * * *');
            for (const url of Object.keys(l1Cache)) {
                console.log(`url: ${url}`);
                const cacheContent = l1Cache[url];
                if (cacheContent != null) {
                    console.log(`cacheTimeStamp: ${cacheContent.cacheTimeStamp}`);
                    console.log(`networkCallTimeStamp: ${cacheContent.networkCallTimeStamp}`);
                    console.log(`superFetchTagList: ${JSON.stringify((cacheContent.superFetchTagList || []).sort())}`);
                    console.log('network payload', cacheContent.networkPayload, '\n\n');
                }
            }
            console.log('* * * * * * * * * * * * * * * * * * * *');
        }
        else if (event.data.type === 'DEBUG_DELETE_L1_CACHE') {
            // Run this section by calling this in the browser console:
            // navigator.serviceWorker.controller.postMessage({ type: 'DEBUG_DELETE_L1_CACHE' });
            for (const key of Object.keys(l1Cache)) {
                delete l1Cache[key];
            }
            console.log('* * * * * * *  Deleted L1 cache * * * * * * *');
        }
        else if (event.data.type === 'DEBUG_DELETE_ENCRYPTION_KEYS') {
            // Run this section by calling this in the browser console:
            // navigator.serviceWorker.controller.postMessage({ type: 'DEBUG_DELETE_ENCRYPTION_KEYS' });
            serviceWorkerEncryptionKeys = undefined;
            console.log('* * * * * * *  Deleted Encryption Keys * * * * * * *');
        }
    };
}
/**
 * Figures out if there are values in newValues that existingValues doesn't have.
 * @param newValues The array of values that you may want to add to the existing values
 * @param existingValues The array of values you will be adding to
 * @returns true if there are values in newValues that do not yet exist in existingValues.
 */
function arrayNeedsUpdate(existingValues, newValues) {
    if (newValues == null) {
        return false;
    }
    else {
        if (newValues.length === 0) {
            return false;
        }
        if (existingValues == null) {
            return true;
        }
    }
    for (const newValue of newValues) {
        if (!existingValues.includes(newValue)) {
            return true;
        }
    }
    return false;
}
/**
 * Combines two arrays together but also ensures that there will not be any duplicates in the returned array.
 * @param arr1 The first array
 * @param arr2 The second array
 * @returns A new array with all of the values in both the first and second arrays.
 */
function combineArrays(arr1, arr2) {
    if (arr1 == null) {
        if (arr2 == null) {
            //arr1 == null && arr2 == null
            return [];
        }
        else {
            //arr1 == null && arr2 != null
            return arr2;
        }
    }
    else if (arr2 == null) {
        //arr1 != null && arr2 == null
        return arr1;
    }
    const set1 = new Set(arr1);
    for (const item of arr2) {
        set1.add(item);
    }
    return Array.from(set1.values());
}
function serializeResponse(response) {
    return __awaiter(this, void 0, void 0, function* () {
        const arrayBuffer = yield response.arrayBuffer();
        const arrayBufferString = arrayBufferToString(arrayBuffer);
        return {
            headers: Array.from(response.headers.entries()),
            status: response.status,
            statusText: response.statusText,
            arrayBufferAsString: arrayBufferString
        };
    });
}
export function deserializeResponse(serializedResponse) {
    return new Response(stringToArrayBuffer(serializedResponse.arrayBufferAsString), {
        status: serializedResponse.status,
        statusText: serializedResponse.statusText,
        headers: new Headers(serializedResponse.headers)
    });
}
/**
 * This function only searches caches created by the mfe utils service worker
 * @param cacheNameSuffix DEPRECATED PARAMETER, we only kept this here to prevent a breaking change. We now get the cache name via the getDynamicCacheFunction
 * @param urlGlob a glob string or string literal to determine which files to delete.
 * NOTE: that special unicode characters are not allowed in urls and will need to be converted
 * Ex: ú = %C3%BA refer to https://r12a.github.io/app-conversion/ after conversion check the section "percent encoding for url's"
 * @returns True if no issues occured, false if we were unable to delete a matching cache entry
 * True is returned if no match is found to delete
 */
export function deleteFromCacheByUrl(cacheNameSuffix, urlGlob) {
    // No longer needed but removing the parameter would be a breaking change
    // const CACHE_NAME = `${CACHE_ROOT}-${cacheNameSuffix}`;
    let isDeleted = true;
    const regExp = globToRegExp(urlGlob, { globstar: true });
    return getDynamicCacheName().then((dynamicCacheName) => {
        return caches.open(dynamicCacheName).then((cache) => {
            // Cache keys are the intercepted requests, they contain the URL of the request
            return cache.keys().then((keys) => {
                return Promise.all(keys.map((request) => {
                    // See if it matches the given urlGlob with regex
                    if (regExp.test(request.url)) {
                        return cache.delete(request).then((deletionSuccess) => {
                            //Now that it's been deleted from the L2 cache
                            //delete it from the L1 cache.
                            delete l1Cache[request.url];
                            // Sets isDeleted to false if deletion fails
                            isDeleted = deletionSuccess && isDeleted;
                            return Promise.resolve(isDeleted);
                        });
                    }
                    else {
                        // No match, just return the current isDeleted value
                        return Promise.resolve(isDeleted);
                    }
                })).then((results) => {
                    // TODO: wouldn't results contain an array of booleans? It's possible that some deletions succeeded and some failed.
                    // So shouldn't we go through the results and return the result combined? For example: return (results[0][0] && results[0][1] && results[0][2]) => (return (true && true && false)
                    return Promise.resolve(isDeleted);
                });
            });
        });
    });
}
/**
 * If mfe-utils updates the CACHE_ROOT (contains our version) or the client-side updates the cache name
 * then this function will find the old service worker caches and delete them
 * This should be called in the activate event of the service worker file
 * @param cacheNameSuffix the cache name used for the createFetchInterceptor function
 * @param staticCacheName the static cache name used for the createStaticCache function
 */
export function deleteOldCaches(cacheNameSuffix, staticCacheName) {
    const CACHE_NAME = `${CACHE_ROOT}-${cacheNameSuffix}`;
    const STATIC_CACHE_NAME = `${STATIC_CACHE_ROOT}-${staticCacheName}`;
    // Get the list of cache keys (cache names)
    caches.keys().then((cacheList) => {
        // Don't stop until all complete
        return Promise.all(cacheList.map((cacheName) => {
            // is the cache key different than the
            // current cache name and has the same root?
            if ((CACHE_NAME !== cacheName && cacheName.startsWith(CACHE_ROOT))
                || (STATIC_CACHE_NAME !== cacheName && cacheName.startsWith(STATIC_CACHE_ROOT))) {
                // if yes, then delete it.
                return caches.delete(cacheName);
            }
        }));
    });
}
/**
 * Creates the JSON Web Key to use for decryption and encryption of our data.
 * For encryption we search for the active key, and for decryption we search for whatever key we used to encrypt even if it's inactive
 * @param encryptionKeyId - The id of the encryption key used to encrpyt the data
 * @returns The JSON Web Key we will use for encryption, or nothing if there is no matching key.
 */
function getRawFormatKey(encryptionKeyId) {
    if (serviceWorkerEncryptionKeys == null) {
        return undefined;
    }
    //console.log('**** encryption KeyId, ', encryptionKeyId);
    const encryptionKey = serviceWorkerEncryptionKeys.keys.find((key) => {
        //console.log('**** key to match: ', key.keyId);
        return encryptionKeyId === key.keyId;
    });
    if (encryptionKey != null) {
        const hex = encryptionKey.value;
        // Goes through the hex value pairs and pushes them into an array with their integer value
        // Ex: Input = '7b88' Output = [123, 136]
        const hexStringAsIntArray = new Uint8Array(hex.match(/[\da-f]{2}/gi).map((hexCharacterPair) => {
            // With hex = '7b88' here we would process 7b first and then 88. We go through each pair in the given hex string
            return parseInt(hexCharacterPair, 16);
        }));
        // Returns the same data as a Buffer Like Object
        return hexStringAsIntArray.buffer;
    }
    else {
        return;
    }
}
/**
 * Searches for a url in the cacheUrlTimeouts object's keys and regex values.
 * If a url is not found we retry in case there is a timing issue
 * Note: Timing issues would likely be properly resolved by using the promises returned from registerTimeouts and setEncryptionKeys
 * within http.ts
 * @param url the url to search for a match within the cacheTimeouts object
 * @param cacheUrlTimeouts the object to search within for a matching url key / glob
 * @returns This will return a promise with true if the url is found or false if it isn't
 */
function isInCacheUrlTimeoutsList(url, cacheUrlTimeouts) {
    let urlTimeout = searchCacheUrlTimeouts(url, cacheUrlTimeouts);
    if (urlTimeout == null) {
        // If we end up here we are either experiencing a timing issue or the url is not being registered at all
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                urlTimeout = searchCacheUrlTimeouts(url, cacheUrlTimeouts);
                if (urlTimeout == null) {
                    setTimeout(() => {
                        urlTimeout = searchCacheUrlTimeouts(url, cacheUrlTimeouts);
                        resolve(urlTimeout != null);
                    }, 200);
                }
                else {
                    resolve(urlTimeout != null);
                }
            }, 100);
        });
    }
    else {
        return Promise.resolve(urlTimeout != null);
    }
}
/**
 * Searches for a matching url key in the cacheUrlTimeouts object, or a regex value that passes with the given url
 * @param url the url to search for a match within the cacheTimeouts object
 * @param cacheUrlTimeouts the object to search within for a matching url key / glob
 * @returns This will return true if the url is found and false if it isn't
 */
function searchCacheUrlTimeouts(url, cacheUrlTimeouts) {
    // check if the url literal is a key
    if (cacheUrlTimeouts[url] != null) {
        return cacheUrlTimeouts[url];
    }
    // If it's not in there as a literal then it can only be registered with a url glob
    for (const urlGlobKey in cacheUrlTimeouts) {
        if (cacheUrlTimeouts[urlGlobKey].urlRegex.test(url)) {
            cacheUrlTimeouts[url] = cacheUrlTimeouts[urlGlobKey];
            return cacheUrlTimeouts[url];
        }
    }
}
// Creates a new response from a fresh response payload and adds the properties
// upToDate to show the data is fresh and a timestamp so we know when it was requested
function createCachedTimeStampedPayloadFromNetworkPayload(networkPayload, superFetchTagList) {
    const timeStamp = new Date().toJSON();
    networkPayload.networkCallTimeStamp = networkPayload.cacheTimeStamp;
    return {
        networkCallTimeStamp: timeStamp,
        cacheTimeStamp: timeStamp,
        networkPayload: networkPayload,
        superFetchTagList: superFetchTagList
    };
}
// Converts a response that we recieved over the network to a response whose payload is a timeStampedPayload
function addNetworkcallTimestampToFreshResponse(freshResponse) {
    const contentType = freshResponse.headers.get('content-type');
    return new Promise((resolve, reject) => {
        if (freshResponse.status >= 200 && freshResponse.status < 300 && (contentType === null || contentType === void 0 ? void 0 : contentType.indexOf('application/json')) !== -1) {
            freshResponse.clone().json().then((responsePayload) => {
                const timeStamp = new Date().toJSON();
                const timeStampedPayload = {
                    networkCallTimeStamp: timeStamp,
                    networkPayload: responsePayload
                };
                const jsonBlob = new Blob([JSON.stringify(timeStampedPayload)], { type: 'application/json' });
                const init = { 'status': freshResponse.status, 'statusText': freshResponse.statusText, 'headers': freshResponse.headers };
                resolve(new Response(jsonBlob, init));
            });
        }
        else {
            resolve(freshResponse);
        }
    });
}
function arrayBufferToString(arrayBufferToConvert) {
    const bufferView = new DataView(arrayBufferToConvert);
    let arrayBufferAsString = '';
    for (let i = 0; i < bufferView.byteLength; i++) {
        arrayBufferAsString += String.fromCharCode(bufferView.getUint8(i));
    }
    return arrayBufferAsString;
}
function stringToArrayBuffer(stringToConvert) {
    const buf = new ArrayBuffer(stringToConvert.length);
    const bufView = new DataView(buf);
    for (let i = 0; i < stringToConvert.length; i++) {
        bufView.setUint8(i, stringToConvert.charCodeAt(i));
    }
    return buf;
}
/**
 * @param jsonStringToDecrypt This should be the JSON from a response that was stored in the cache by this service worker
 * The JSON is expected to contain properties (iv and data) that are set before we encrypt the data
 * @param cryptoKey The cryptoKey we will use to decrypt the string
 * @returns This will return the decrypted JSON as an object
 */
function decryptJSON(jsonStringToDecrypt, iv, cryptoKey) {
    // First we convert the string back to an arraybuffer
    const buf = stringToArrayBuffer(jsonStringToDecrypt);
    // Set the encyption algorithn with the proper IV value
    const encryptionAlgorithm = {
        name: 'AES-GCM',
        iv: Uint8Array.from(iv),
        length: 256
    };
    // Decrypt and return the JSON body of the decrypted response
    return crypto.subtle.decrypt(encryptionAlgorithm, cryptoKey, buf).then((bytes) => {
        return Promise.resolve(JSON.parse(new TextDecoder().decode(bytes)));
    });
}
/**
 * @param JSONStringToEncrypt This can be any string, but we are using it for JSON strings only at this time.
 * @param cryptoKey The cryptoKey that we will use to encrypt the string
 * @returns This will return the encrypted JSON as a string
 */
function encryptJSON(JSONStringToEncrypt, encryptionAlgorithm, cryptoKey) {
    const bytes = new TextEncoder().encode(JSONStringToEncrypt);
    // At this point we have all the data we need to encrpyt the JSON
    return crypto.subtle.encrypt(encryptionAlgorithm, cryptoKey, bytes).then((encryptedData) => {
        return arrayBufferToString(encryptedData);
    });
}
/**
 * @param jsonStringToEncrypt The JSON of the response's payload
 * @param cryptoKey Crypto Key that will be used for encryption
 * @returns an object with encrypted JSON and the required data to decrypt it later on
 */
function getTimeStampedEncryptedPayload(timeStampedPayload, cryptoKey, serviceWorkerEncryptionKeys, request, cacheUrlTimeouts) {
    const encryptionAlgorithm = {
        name: 'AES-GCM',
        iv: crypto.getRandomValues(new Uint8Array(12)),
        length: 256
    };
    // At this point we have all the data we need to encrpyt the JSON
    return encryptJSON(JSON.stringify(timeStampedPayload), encryptionAlgorithm, cryptoKey).then((encryptedData) => {
        const urlTimeout = searchCacheUrlTimeouts(request.url, cacheUrlTimeouts);
        //The object created here is what gets written to the L2 cache
        return {
            networkCallTimeStamp: timeStampedPayload.networkCallTimeStamp,
            cacheTimeStamp: timeStampedPayload.cacheTimeStamp,
            // Set the id for the encryption key that was used
            encryptionKeyId: serviceWorkerEncryptionKeys.activeKeyId,
            // Set the randomly generated IV value for unique encrpytion
            iv: Array.from(encryptionAlgorithm.iv),
            //This data buffer is encrypted
            networkPayload: encryptedData,
            stale: (urlTimeout === null || urlTimeout === void 0 ? void 0 : urlTimeout.stale) || 0,
            expires: (urlTimeout === null || urlTimeout === void 0 ? void 0 : urlTimeout.expires) || 0,
            superFetchTagList: timeStampedPayload.superFetchTagList
        };
    });
}
/**
 * Takes a response object and the payload of the same response to create a new encrypted response to put in the cache
 * @param cachedTimeStampedPayload The payload of the response from response.clone().json()
 * @param response The orignal response that you want to have encrpyted. We need this to set the same headers and other values
 * @returns If encryption is successful then we return a new response and the body json has three properties
 * {
 *  data: The given body json but it will be encrypted
 *  encryptionKeyId: The id of the encryption key used to encrypt this data
 *  iv: A randomly generated array that is used to create unique encryption patterns
 * }
 *
 * Otherwise, we return nothing.
 */
function getNewResponseWithTimeStampedEncryptedPayload(cachedTimeStampedPayload, response, request, cacheUrlTimeouts) {
    if (serviceWorkerEncryptionKeys == null) {
        console.error('The encryption keys are not valid so we cannot encrypt and cache this response: ', response);
        return Promise.resolve(undefined);
    }
    // creating a constant here so that we don't have to do the null check twice
    const swEncryptionKeys = serviceWorkerEncryptionKeys;
    const encryptionKeyValueAsArrayBuffer = getRawFormatKey(swEncryptionKeys.activeKeyId);
    if (encryptionKeyValueAsArrayBuffer != null) {
        return crypto.subtle.importKey('raw', encryptionKeyValueAsArrayBuffer, importKeyAlgorithm, true, keyUsages).then((cryptoKey) => {
            // At this point we have all the data we need to encrpyt the JSON
            return getTimeStampedEncryptedPayload(cachedTimeStampedPayload, cryptoKey, swEncryptionKeys, request, cacheUrlTimeouts).then((timeStampedEncryptedPayload) => {
                const jsonBlob = new Blob([JSON.stringify(timeStampedEncryptedPayload)], { type: 'application/json' });
                const init = { 'status': response.status, 'statusText': response.statusText, 'headers': response.headers };
                return Promise.resolve(new Response(jsonBlob, init));
            });
        });
    }
    else {
        return Promise.resolve(undefined);
    }
}
/**
 * Gets fresh response data for a given request and caches the response encrypted
 * @param request The request that we are going to make the fetch call for
 * @param cacheName The cache name passed into the service worker
 * @returns the fresh response from the given request with a payload of type CachedTimeStampPayload
 */
function getFreshTimeStampedResponseAndCache(request, cacheName, cacheUrlTimeouts, superFetchTagList) {
    // const CACHE_NAME = `${CACHE_ROOT}-${cacheName}`;
    return fetchAndRetry(request).then((networkResponse) => {
        if (networkResponse.status === 200) {
            //the responsePayload object here really is an any because it's what came over the network from the server. We don't know what's in there.
            return networkResponse.json().then((networkPayload) => {
                return getDynamicCacheName().then((dynamicCacheName) => {
                    return caches.open(dynamicCacheName).then((cache) => {
                        const timeStampedPayload = createCachedTimeStampedPayloadFromNetworkPayload(networkPayload, superFetchTagList);
                        return getNewResponseWithTimeStampedEncryptedPayload(timeStampedPayload, networkResponse, request, cacheUrlTimeouts).then((resWithTimeStampedEncryptedPayload) => {
                            if (resWithTimeStampedEncryptedPayload != null) {
                                //put it into the L2 cache
                                cache.put(request.url, resWithTimeStampedEncryptedPayload.clone());
                                //put it into the L1 cache too
                                //console.log('fetchAndRetry timestamped payload: ', timeStampedPayload);
                                l1Cache[request.url] = timeStampedPayload;
                            }
                            else {
                                // If we get here then the service worker encryption keys didn't have the data structure expected or were missing some properties
                                // This means we will never be putting anything in the cache because we were unable to encrypt the data
                                console.error('An unexpected error occurred with service worker caching');
                            }
                            return createResponseFromPayload(timeStampedPayload, networkResponse);
                        });
                    });
                });
            });
        }
        else {
            return networkResponse;
        }
    });
}
function createResponseFromPayload(payload, response) {
    const jsonBlob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    const init = { 'status': response.status, 'statusText': response.statusText, 'headers': response.headers };
    return new Response(jsonBlob, init);
}
/**
 * Calculates if something is timed out based on the data and time it takes to timeout in minutes
 * @param timeStamp string date of when the data was retreived over the network
 * @param timeout number of minutes until the data is timedout, stale or expired in this case
 */
function isTimedout(timeStamp, timeout) {
    const milisecondsDifference = Date.now() - new Date(timeStamp).getTime();
    const differenceInCorrectUnits = milisecondsDifference / 60000;
    if (differenceInCorrectUnits > timeout) {
        //If we are less than the value then we will display the data and then update the cache in the background
        return true;
    }
    else {
        //This is too old to display, we will just return an empty json response and display it after we fetch over the network
        return false;
    }
}
// Try to fetch the request
// If it fails then try again after 1 second, if it fails try again in 2 seconds
function fetchAndRetry(request) {
    let modifiedRequest;
    // Here we need to remove the superfetch or superfetchtaglist header to prevent unneeded preflight requests
    if (request.headers.has('superfetch') || request.headers.has('superfetchtaglist')) {
        const modifiedHeaders = new Headers(Array.from(request.headers.entries()).filter((pair) => {
            return pair[0] !== 'superfetch' && pair[0] !== 'superfetchtaglist';
        }));
        modifiedRequest = new Request(request, { headers: modifiedHeaders });
    }
    return fetch(modifiedRequest || request).catch((error) => {
        // You can't use requests twice if the body is used, this can happen when errors are thrown
        if (request.bodyUsed) {
            throw error;
        }
        // Wait 1 second
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                fetch(request).then((res) => {
                    resolve(res);
                }).catch((error) => {
                    // You can't use requests twice if the body is used, this can happen when errors are thrown
                    if (request.bodyUsed) {
                        throw error;
                    }
                    // Wait 2 seconds
                    setTimeout(() => {
                        fetch(request).then((res) => {
                            resolve(res);
                        }).catch((error) => {
                            reject(error);
                        });
                    }, 2000);
                });
            }, 1000);
        });
    });
}
export function isStale(cacheTimeStamp, staleTimeout) {
    return isTimedout(cacheTimeStamp, staleTimeout);
}
export function isExpired(cacheTimeStamp, expiredTimeout) {
    return isTimedout(cacheTimeStamp, expiredTimeout);
}
//first checks l1Cache, and then checks l2Cache.
function getFromCache(url) {
    return __awaiter(this, void 0, void 0, function* () {
        if (l1Cache[url] != null) {
            return l1Cache[url];
        }
        else {
            //It wasn't in the l1 cache. I need to look in the l2 cache.
            const responseFromCache = yield caches.match(url);
            if (responseFromCache == null) {
                return;
            }
            const encryptedPayload = yield responseFromCache.json();
            // Keys could become compromised, and if that happens we want
            // to remove the data using compromised or inactive keys as soon as possible
            // if (key is rotated out) {
            // const cache: Cache = await caches.open(dynamicCacheName);
            // This would be for a single request ! vs
            // should we walk the whole cache and delete everything that uses the old key?
            // } else if (key Is Missing) {
            // Prioritize loading the mfe's and the page and then...
            // walk the cache and purge everything that's not using a key in rotation / the active key
            // If we can do this in the background that would be ideal, maybe a timeout?
            // If this is not possible then evict on access. Is this acceptable???
            // }
            const encryptionKeyValueAsArrayBuffer = getRawFormatKey(encryptedPayload.encryptionKeyId);
            if (encryptionKeyValueAsArrayBuffer != null) {
                const cryptoKey = yield crypto.subtle.importKey('raw', encryptionKeyValueAsArrayBuffer, importKeyAlgorithm, true, keyUsages);
                const decryptedPayload = yield decryptJSON(encryptedPayload.networkPayload, encryptedPayload.iv, cryptoKey);
                //console.log('getFromcache timestamped payload: ', decryptedPayload);
                l1Cache[url] = decryptedPayload;
                // Keys could become compromised, and if that happens we want
                // to remove the data using compromised or inactive keys as soon as possible
                return decryptedPayload;
            }
        }
    });
}
export function getDynamicCacheName() {
    return __awaiter(this, void 0, void 0, function* () {
        // If the cache name is set locally just return it
        if ((dynamicCacheName === null || dynamicCacheName === void 0 ? void 0 : dynamicCacheName.length) !== 0) {
            return Promise.resolve(dynamicCacheName);
            // else we need to determine what the cache name is by using our cache root string
        }
        else {
            // Get the list of cache keys (cache names)
            yield caches.keys().then((cacheList) => {
                cacheList.forEach((cacheName) => {
                    if (cacheName.startsWith(CACHE_ROOT)) {
                        dynamicCacheName = cacheName;
                        return;
                    }
                });
            }).catch(() => {
                return Promise.reject('The cache name could not be found');
            });
            if (dynamicCacheName != null) {
                return Promise.resolve(dynamicCacheName);
            }
            else {
                return Promise.reject('The cache name could not be found');
            }
        }
    });
}
